/**
 * Jobs Slice
 * Manages job state and async operations
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { jobsApi } from '../../utils/api/index.js';
import { extractPaginatedList, normalizePagination } from '../../utils/pagination.js';
import { getErrorMessage } from '../../utils/api/errors.js';

/**
 * Initial state
 */
const initialState = {
  jobs: [],
  currentJob: null,
  stats: null,
  loading: false,
  error: null,
  filters: {
    page: 1,
    limit: 10,
    status: null,
    source: null,
    jobType: null,
    search: '',
  },
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  },
};

/**
 * Async thunk: Fetch all jobs
 */
export const fetchJobs = createAsyncThunk(
  'jobs/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await jobsApi.getAll(params);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch jobs'));
    }
  }
);

/**
 * Async thunk: Fetch job by ID
 */
export const fetchJobById = createAsyncThunk(
  'jobs/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await jobsApi.getById(id);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch job'));
    }
  }
);

/**
 * Async thunk: Fetch job by JobScribe ID
 */
export const fetchJobByJobscribeId = createAsyncThunk(
  'jobs/fetchByJobscribeId',
  async (jobscribeId, { rejectWithValue }) => {
    try {
      const response = await jobsApi.getByJobscribeId(jobscribeId);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch job'));
    }
  }
);

/**
 * Async thunk: Create job
 */
export const createJob = createAsyncThunk(
  'jobs/create',
  async (jobData, { rejectWithValue }) => {
    try {
      const response = await jobsApi.create(jobData);
      return response;
    } catch (error) {
      if (error?.status === 409 || error?.code === 'DUPLICATE_JOB_CONTENT' || error?.errors?.code === 'DUPLICATE_JOB_CONTENT') {
        return rejectWithValue({
          message: getErrorMessage(error, 'A duplicate job already exists.'),
          code: 'DUPLICATE_JOB_CONTENT',
          existingJob: error?.errors?.existingJob || null,
        });
      }
      return rejectWithValue(getErrorMessage(error, 'Failed to create job'));
    }
  }
);

/**
 * Async thunk: Update job
 */
export const updateJob = createAsyncThunk(
  'jobs/update',
  async ({ id, jobData }, { rejectWithValue }) => {
    try {
      const response = await jobsApi.update(id, jobData);
      return response;
    } catch (error) {
      if (error?.status === 409 || error?.code === 'DUPLICATE_JOB_CONTENT' || error?.errors?.code === 'DUPLICATE_JOB_CONTENT') {
        return rejectWithValue({
          message: getErrorMessage(error, 'A duplicate job already exists.'),
          code: 'DUPLICATE_JOB_CONTENT',
          existingJob: error?.errors?.existingJob || null,
        });
      }
      return rejectWithValue(getErrorMessage(error, 'Failed to update job'));
    }
  }
);

/**
 * Async thunk: Delete job
 */
export const deleteJob = createAsyncThunk(
  'jobs/delete',
  async (id, { rejectWithValue }) => {
    try {
      await jobsApi.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete job'));
    }
  }
);

/**
 * Async thunk: Update job status
 */
export const updateJobStatus = createAsyncThunk(
  'jobs/updateStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await jobsApi.updateStatus(id, status);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update job status'));
    }
  }
);

/**
 * Async thunk: Bulk update job statuses
 */
export const bulkUpdateJobStatus = createAsyncThunk(
  'jobs/bulkUpdateStatus',
  async ({ ids, status }, { rejectWithValue }) => {
    try {
      const response = await jobsApi.bulkUpdateStatus(ids, status);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to bulk update job statuses'));
    }
  }
);

/**
 * Async thunk: Fetch job statistics
 */
export const fetchJobStats = createAsyncThunk(
  'jobs/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await jobsApi.getStats();
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch statistics'));
    }
  }
);

/**
 * Jobs slice
 */
const jobsSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {
    /**
     * Clear current job
     */
    clearCurrentJob: (state) => {
      state.currentJob = null;
      state.error = null;
    },

    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Set filters
     */
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    /**
     * Reset filters
     */
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },

    /**
     * Set pagination
     */
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all jobs
      .addCase(fetchJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobs.fulfilled, (state, action) => {
        state.loading = false;
        const { data, pagination } = extractPaginatedList(action.payload);
        state.jobs = data;
        if (pagination) {
          state.pagination = normalizePagination(pagination, state.pagination);
        }
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.jobs = [];
      })

      // Fetch job by ID
      .addCase(fetchJobById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentJob = action.payload;
      })
      .addCase(fetchJobById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.currentJob = null;
      })

      // Fetch job by JobScribe ID
      .addCase(fetchJobByJobscribeId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobByJobscribeId.fulfilled, (state, action) => {
        state.loading = false;
        state.currentJob = action.payload;
      })
      .addCase(fetchJobByJobscribeId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.currentJob = null;
      })

      // Create job
      .addCase(createJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createJob.fulfilled, (state, action) => {
        state.loading = false;
        const newJob = action.payload?.data || action.payload;
        if (newJob) {
          state.jobs.unshift(newJob); // Add to beginning
        }
      })
      .addCase(createJob.rejected, (state, action) => {
        state.loading = false;
        state.error =
          typeof action.payload === 'object' && action.payload?.message
            ? action.payload.message
            : action.payload;
      })

      // Update job
      .addCase(updateJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateJob.fulfilled, (state, action) => {
        state.loading = false;
        const updatedJob = action.payload?.data || action.payload;
        if (updatedJob) {
          const index = state.jobs.findIndex((j) => j.id === updatedJob.id);
          if (index !== -1) {
            state.jobs[index] = updatedJob;
          }
          if (state.currentJob?.id === updatedJob.id) {
            state.currentJob = updatedJob;
          }
        }
      })
      .addCase(updateJob.rejected, (state, action) => {
        state.loading = false;
        state.error =
          typeof action.payload === 'object' && action.payload?.message
            ? action.payload.message
            : action.payload;
      })

      // Delete job
      .addCase(deleteJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteJob.fulfilled, (state, action) => {
        state.loading = false;
        state.jobs = state.jobs.filter((j) => j.id !== action.payload);
        if (state.currentJob?.id === action.payload) {
          state.currentJob = null;
        }
      })
      .addCase(deleteJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update job status
      .addCase(updateJobStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateJobStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updatedJob = action.payload?.data || action.payload;
        if (updatedJob) {
          const index = state.jobs.findIndex((j) => j.id === updatedJob.id);
          if (index !== -1) {
            state.jobs[index] = updatedJob;
          }
          if (state.currentJob?.id === updatedJob.id) {
            state.currentJob = updatedJob;
          }
        }
      })
      .addCase(updateJobStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Bulk update job status
      .addCase(bulkUpdateJobStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdateJobStatus.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(bulkUpdateJobStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch statistics
      .addCase(fetchJobStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload?.data || action.payload;
      })
      .addCase(fetchJobStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearCurrentJob,
  clearError,
  setFilters,
  resetFilters,
  setPagination,
} = jobsSlice.actions;

export default jobsSlice.reducer;
